import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Slide,
  Button,
  Typography,
  Box,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import RefreshIcon from "@material-ui/icons/Refresh";
import axios from "axios";
import envConfig from "../../../bin/env.config";
import { useSelector } from "react-redux";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const STATUS_COLORS = {
  running: "#4caf50",
  completed: "#2196f3",
  stopped: "#ff9800",
  error: "#f44336",
  interrupted: "#9e9e9e",
  pending: "#9e9e9e",
};

function StatusChip({ status }) {
  if (!status) return null;
  return (
    <Chip
      label={status.toUpperCase()}
      size="small"
      style={{
        backgroundColor: STATUS_COLORS[status] || "#9e9e9e",
        color: "#fff",
        fontWeight: 600,
      }}
    />
  );
}

export default function DialogAutoMap({ open, onClose, project }) {
  const token = useSelector((state) => state.session.token);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);
  const pollingRef = useRef(null);

  const apiBase = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/auto-map`;

  const fetchStatus = useCallback(async () => {
    if (!project) return;
    try {
      const res = await axios.get(`${apiBase}/status`, {
        params: { project_id: project.id },
        headers: { Authorization: token },
      });
      setJobData(res.data.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch auto-map status:", err);
    }
  }, [project, token, apiBase]);

  useEffect(() => {
    if (open && project) {
      fetchStatus();
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [open, project, fetchStatus]);

  useEffect(() => {
    if (open && jobData && jobData.status === "running") {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(fetchStatus, 5000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [open, jobData, fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${apiBase}/start`,
        { project_id: project.id },
        { headers: { Authorization: token } }
      );
      setJobData(res.data.data);
      // Start polling
      if (!pollingRef.current) {
        pollingRef.current = setInterval(fetchStatus, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to start job");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!jobData) return;
    setLoading(true);
    try {
      await axios.post(
        `${apiBase}/stop`,
        { job_id: jobData.id },
        { headers: { Authorization: token } }
      );
      setTimeout(fetchStatus, 1000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to stop job");
    } finally {
      setLoading(false);
    }
  };

  const handleMergeDuplicates = async () => {
    setDedupeLoading(true);
    setError(null);
    setDedupeResult(null);
    try {
      const preview = await axios.post(
        `${apiBase}/dedupe-duplicates`,
        { dry_run: true },
        { headers: { Authorization: token } }
      );
      const data = preview.data.data;
      const clusterCount = data.clusters?.length || 0;
      const pairCount = (data.clusters || []).reduce(
        (n, c) => n + (c.merge_ids?.length || 0),
        0
      );

      if (pairCount === 0) {
        setDedupeResult({ message: "No duplicate LLM groups found." });
        return;
      }

      const ok = window.confirm(
        `Found ${clusterCount} cluster(s), ${pairCount} merge(s).\n` +
          `Exact: ${data.summary?.exact_pairs ?? 0}, Fuzzy proposed: ${data.summary?.fuzzy_pairs_proposed ?? 0}, Fuzzy rejected: ${data.summary?.fuzzy_pairs_rejected ?? 0}.\n\n` +
          `Apply merges now?`
      );
      if (!ok) {
        setDedupeResult({ dry_run: true, ...data });
        return;
      }

      const apply = await axios.post(
        `${apiBase}/dedupe-duplicates`,
        { dry_run: false },
        { headers: { Authorization: token } }
      );
      setDedupeResult(apply.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Dedupe failed");
    } finally {
      setDedupeLoading(false);
    }
  };

  const isRunning = jobData && jobData.status === "running";
  const progress =
    jobData && jobData.total_to_process > 0
      ? Math.round((jobData.processed / jobData.total_to_process) * 100)
      : 0;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>LLM Auto-Map — {project && project.name}</span>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Status & Progress */}
        <Box mb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="subtitle2">Status:</Typography>
              {jobData ? <StatusChip status={jobData.status} /> : <Typography variant="body2" color="textSecondary">No active job</Typography>}
            </Box>
            <IconButton size="small" onClick={fetchStatus} title="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>

          {jobData && (
            <>
              <LinearProgress
                variant="determinate"
                value={progress}
                style={{ height: 8, borderRadius: 4, marginBottom: 8 }}
              />
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" style={{ gap: 16 }}>
                <Typography variant="body2">
                  Processed: <b>{jobData.processed}</b> / {jobData.total_to_process}
                </Typography>
                <Typography variant="body2" style={{ color: "#4caf50" }}>
                  Matched: <b>{jobData.matched}</b>
                </Typography>
                <Typography variant="body2" style={{ color: "#ff9800" }}>
                  Not Found: <b>{jobData.not_found}</b>
                </Typography>
                <Typography variant="body2" style={{ color: "#f44336" }}>
                  Errors: <b>{jobData.errors}</b>
                </Typography>
              </Box>
              {jobData.error_message && (
                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                  Error: {jobData.error_message}
                </Typography>
              )}
              {jobData.started_at && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                  Started: {new Date(jobData.started_at).toLocaleString()}
                  {jobData.finished_at && ` — Finished: ${new Date(jobData.finished_at).toLocaleString()}`}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Recent Logs */}
        {jobData && jobData.recent_logs && jobData.recent_logs.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Recent Activity ({jobData.recent_logs.length} entries)
            </Typography>
            <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobData.recent_logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                        {new Date(log.created_at).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>{log.company_report_tool_product_id}</TableCell>
                      <TableCell>{log.step}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          variant="outlined"
                          color={log.status === "success" ? "primary" : log.status === "failed" ? "secondary" : "default"}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: "0.75rem", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {log.detail && typeof log.detail === "object"
                          ? Object.entries(log.detail).map(([k, v]) => `${k}: ${v}`).join(", ")
                          : String(log.detail || "")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {dedupeResult && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Dedupe result
            </Typography>
            {dedupeResult.message && (
              <Typography variant="body2">{dedupeResult.message}</Typography>
            )}
            {dedupeResult.dry_run && Array.isArray(dedupeResult.clusters) && (
              <Typography variant="body2">
                Preview: {dedupeResult.clusters.length} cluster(s),{" "}
                {(dedupeResult.clusters || []).reduce(
                  (n, c) => n + (c.merge_ids?.length || 0),
                  0
                )}{" "}
                merge(s) — not applied.
              </Typography>
            )}
            {Array.isArray(dedupeResult.merged) && (
              <Typography variant="body2">
                Merged: <b>{dedupeResult.merged.length}</b>
                {" · "}
                Skipped: <b>{dedupeResult.skipped?.length || 0}</b>
                {" · "}
                Errors: <b>{dedupeResult.errors?.length || 0}</b>
              </Typography>
            )}
            {Array.isArray(dedupeResult.errors) && dedupeResult.errors.length > 0 && (
              <Typography variant="body2" color="error" style={{ marginTop: 4 }}>
                {dedupeResult.errors
                  .slice(0, 3)
                  .map((e) => `${e.keep_id}←${e.merge_id}: ${e.message}`)
                  .join("; ")}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayArrowIcon />}
          onClick={handleStart}
          disabled={loading || dedupeLoading || isRunning}
        >
          Start
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<StopIcon />}
          onClick={handleStop}
          disabled={loading || dedupeLoading || !isRunning}
        >
          Stop
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleMergeDuplicates}
          disabled={loading || dedupeLoading || isRunning}
        >
          {dedupeLoading ? "Merging…" : "Merge Duplicates"}
        </Button>
        <Button onClick={onClose} color="default" disabled={dedupeLoading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
